import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FileSource, OpenedDocument } from '../filesource/types';
import { createViewerStore } from '../store/viewer-store';
import { ViewerStoreProvider, useViewer, useViewerStoreHandle } from '../store/context';
import { loadThemePref } from '../hooks/useTheme';
import { useTheme } from '../hooks/useTheme';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useMermaid } from '../hooks/useMermaid';
import { useFind } from '../hooks/useFind';
import { useReadingProgress } from '../hooks/useReadingProgress';
import { Header } from './Header';
import { TableOfContents } from './TableOfContents';
import { DocumentView } from './DocumentView';
import { EmptyState } from './EmptyState';
import { FindBar } from './FindBar';
import { DefaultAppPrompt } from './DefaultAppPrompt';
import { ErrorBoundary } from './ErrorBoundary';
import { Editor } from './Editor';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { useScrollSync } from '../hooks/useScrollSync';
import { FeedbackDialog } from './FeedbackDialog';
import { DEFAULT_APP_CONFIG, type AppConfig } from '../config';

export interface AppProps {
  source: FileSource;
  config?: AppConfig;
}

function loadInitialZoom(): number {
  try {
    const z = Number(localStorage.getItem('markroom.zoom'));
    if (Number.isFinite(z) && z >= 0.6 && z <= 2.4) return z;
  } catch {
    // ignore
  }
  return 1;
}

function loadInitialReadingWidth(): boolean {
  try {
    return localStorage.getItem('markroom.readingWidth') === '1';
  } catch {
    // ignore
  }
  return false;
}

/** Public entry: creates the store and provides it to the shell. */
export function App({ source, config = DEFAULT_APP_CONFIG }: AppProps) {
  const [store] = useState(() => {
    const s = createViewerStore(source, loadThemePref());
    const isNarrow = typeof matchMedia !== 'undefined' && matchMedia('(max-width: 767px)').matches;
    s.setState({
      zoom: loadInitialZoom(),
      sidebarOpen: !isNarrow,
      readingWidth: loadInitialReadingWidth(),
    });
    return s;
  });
  return (
    <ViewerStoreProvider store={store}>
      <AppShell source={source} config={config} />
    </ViewerStoreProvider>
  );
}

function AppShell({ source, config }: { source: FileSource; config: AppConfig }) {
  useTheme();

  const store = useViewerStoreHandle();
  const appRef = useRef<HTMLDivElement>(null);
  const docScrollRef = useRef<HTMLElement>(null);
  const [forceDefaultPrompt, setForceDefaultPrompt] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const canSetDefault =
    source.platform === 'macos' || source.platform === 'windows' || source.platform === 'android';

  const status = useViewer((s) => s.status);
  const render = useViewer((s) => s.render);
  const error = useViewer((s) => s.error);
  const zoom = useViewer((s) => s.zoom);
  const readingWidth = useViewer((s) => s.readingWidth);
  const renderVersion = useViewer((s) => s.renderVersion);
  const docName = useViewer((s) => s.doc?.name ?? null);
  const docLocator = useViewer((s) => s.doc?.locator ?? null);

  const { next, prev } = useFind(docScrollRef);
  useMermaid(docScrollRef, renderVersion, render);
  const { scrolled, activeHeading } = useReadingProgress(docScrollRef, renderVersion);

  // --- Editor wiring (Phase A) ---
  useEditorPreview();
  const editMode = useViewer((s) => s.editMode);
  const editBuffer = useViewer((s) => s.editBuffer);
  const isDirty = useViewer((s) => s.isDirty);
  const themePref = useViewer((s) => s.themePref);
  const setEditBuffer = useViewer((s) => s.setEditBuffer);
  const newDocument = useViewer((s) => s.newDocument);
  const save = useViewer((s) => s.save);
  const exportAs = useViewer((s) => s.exportAs);
  const saveError = useViewer((s) => s.saveError);
  const clearSaveError = useViewer((s) => s.clearSaveError);
  // The editor scroller is tracked in state (not a ref) so scroll-sync re-runs
  // once the lazily-mounted CodeMirror registers its scroll element.
  const [editorScrollEl, setEditorScrollEl] = useState<HTMLElement | null>(null);

  const [large, setLarge] = useState(
    () => typeof matchMedia !== 'undefined' && matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(min-width: 1024px)');
    const on = () => {
      setLarge(mq.matches);
    };
    mq.addEventListener('change', on);
    return () => {
      mq.removeEventListener('change', on);
    };
  }, []);

  // Split only exists on large screens; collapse to edit otherwise.
  const effectiveMode = editMode === 'split' && !large ? 'edit' : editMode;
  // The editor only applies to a ready document; when loading/empty/errored the
  // main pane must be visible so those states (and load errors) are never hidden
  // behind a stale editor.
  const isReady = status === 'ready';
  const showEditor = isReady && (effectiveMode === 'edit' || effectiveMode === 'split');
  const showPreview = !isReady || effectiveMode === 'view' || effectiveMode === 'split';
  // Track the system color scheme reactively so the editor theme follows a live
  // OS appearance change while it's open (useTheme only toggles the <html> class).
  const [systemDark, setSystemDark] = useState(
    () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const on = () => {
      setSystemDark(mq.matches);
    };
    mq.addEventListener('change', on);
    return () => {
      mq.removeEventListener('change', on);
    };
  }, []);
  const dark = themePref === 'dark' || (themePref === 'system' && systemDark);

  useScrollSync(editorScrollEl, docScrollRef.current, effectiveMode === 'split');

  // Document find only applies when the preview is visible. Gate the shortcut,
  // and close the bar if the preview is hidden while it's open (edit-only).
  useKeyboardShortcuts(showPreview);
  const findOpen = useViewer((s) => s.findOpen);
  useEffect(() => {
    if (!showPreview && findOpen) store.getState().setFindOpen(false);
  }, [showPreview, findOpen, store]);

  // Scroll position to restore after a watch-triggered live reload.
  const pendingScroll = useRef<number | null>(null);
  // A watched external change received while the buffer was dirty, replayed once
  // editing settles. Cleared when the watched document changes.
  const pendingExternalDoc = useRef<OpenedDocument | null>(null);
  useLayoutEffect(() => {
    if (pendingScroll.current !== null && docScrollRef.current) {
      docScrollRef.current.scrollTop = pendingScroll.current;
      pendingScroll.current = null;
    }
  }, [render]);

  const openFile = useCallback(() => {
    source.pickAndOpen().then(
      (doc) => {
        if (doc) void store.getState().openDocument(doc);
      },
      (err: unknown) => {
        store.getState().setLoadError(err instanceof Error ? err.message : 'Could not open file.');
      },
    );
  }, [source, store]);

  // Initial recents.
  useEffect(() => {
    void store.getState().reloadRecents();
  }, [store]);

  // Cold-start (launch) open + warm OS opens.
  useEffect(() => {
    let active = true;
    void source.consumeLaunchOpen().then((doc) => {
      if (active && doc) void store.getState().openDocument(doc);
    });
    const off = source.subscribeOpenEvents((doc) => {
      void store.getState().openDocument(doc);
    });
    return () => {
      active = false;
      off();
    };
  }, [source, store]);

  // Drag-and-drop (platform-appropriate wiring lives in the adapter).
  useEffect(() => {
    const el = appRef.current;
    if (!el) return;
    return source.attachDropTarget(
      el,
      (doc) => {
        void store.getState().openDocument(doc);
      },
      (message) => {
        store.getState().setLoadError(message);
      },
    );
  }, [source, store]);

  // Live file-watch (desktop): re-render on disk changes, preserving scroll.
  useEffect(() => {
    if (!docLocator || !source.canWatch) return;
    const doc = store.getState().doc;
    if (!doc) return;
    const unwatch = source.watch(doc, (next) => {
      // Don't clobber unsaved edits: stash the external change while the buffer
      // is dirty and replay it once editing settles (see the resume effect).
      if (store.getState().isDirty) {
        pendingExternalDoc.current = next;
        return;
      }
      pendingScroll.current = docScrollRef.current?.scrollTop ?? null;
      void store.getState().openDocument(next);
    });
    return () => {
      pendingExternalDoc.current = null; // drop any pending change for this doc
      unwatch();
    };
  }, [docLocator, source, store]);

  // Resume a watched change that arrived while the buffer was dirty: once the
  // edit is no longer dirty (discarded), load the latest file content (FR-27).
  useEffect(() => {
    if (isDirty) return;
    const pending = pendingExternalDoc.current;
    if (!pending) return;
    pendingExternalDoc.current = null;
    pendingScroll.current = docScrollRef.current?.scrollTop ?? null;
    void store.getState().openDocument(pending);
  }, [isDirty, store]);

  // Browser/PWA unload guard: warn before a reload/close silently drops a dirty
  // buffer (FR-36). Desktop/mobile shells don't fire beforeunload, so this is a
  // no-op there.
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isDirty]);

  // Save shortcuts: Ctrl/Cmd-S → Save, Ctrl/Cmd-Shift-S → Export/Save-As. Only
  // when a document is open; the store actions self-guard, and preventDefault
  // stops the browser's own save dialog.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.key.toLowerCase() !== 's' || !isReady) return;
      e.preventDefault();
      if (e.shiftKey) void exportAs();
      else void save();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isReady, save, exportAs]);

  // Link handling: external links open in the system browser, never inside the
  // app shell. Relative links (e.g. `./other.md`) must NEVER navigate the SPA
  // away — that would reboot the app to the empty state and lose the open
  // document (a session-destroying bug) — so every non-hash link is
  // preventDefault()'d, and relative ones are handed to the store to attempt
  // opening as a sibling document (adapter-dependent; a safe no-op elsewhere).
  useEffect(() => {
    const el = docScrollRef.current;
    if (!el) return;
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement | null)?.closest('a');
      const href = anchor?.getAttribute('href');
      if (!href) return;
      if (href.startsWith('#')) return; // in-document anchor: default scroll
      if (/^(https?:|mailto:|tel:|\/\/)/i.test(href)) {
        e.preventDefault();
        source.openExternalLink(href);
        return;
      }
      e.preventDefault(); // relative link: never navigate the SPA away
      void store.getState().openRelativeLink(href);
    };
    el.addEventListener('click', onClick);
    return () => {
      el.removeEventListener('click', onClick);
    };
  }, [source, store]);

  // Window title.
  useEffect(() => {
    document.title = docName ? `${docName} — MarkRoom` : 'MarkRoom';
  }, [docName]);

  // Persist zoom.
  useEffect(() => {
    try {
      localStorage.setItem('markroom.zoom', String(zoom));
    } catch {
      // ignore
    }
  }, [zoom]);

  // Persist reading-width preference.
  useEffect(() => {
    try {
      localStorage.setItem('markroom.readingWidth', readingWidth ? '1' : '0');
    } catch {
      // ignore
    }
  }, [readingWidth]);

  return (
    <div ref={appRef} className="flex h-screen flex-col bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <Header
        onOpenFile={openFile}
        onNew={() => void newDocument()}
        onFeedback={() => {
          setFeedbackOpen(true);
        }}
        large={large}
        canFind={showPreview}
        scrolled={scrolled}
        activeHeading={activeHeading}
        {...(canSetDefault
          ? {
              onReopenDefault: () => {
                setForceDefaultPrompt(true);
              },
            }
          : {})}
        {...(source.canOpenNewWindow
          ? {
              onNewWindow: () => {
                source.newWindow();
              },
            }
          : {})}
      />
      {saveError && (
        <div
          role="alert"
          className="flex items-center gap-3 border-b border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
        >
          <span className="min-w-0 flex-1 truncate">Couldn’t save: {saveError}</span>
          <button
            type="button"
            onClick={clearSaveError}
            className="mr-btn !min-h-7 shrink-0 !text-red-800 dark:!text-red-200"
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        <TableOfContents />
        {/* `relative` so the floating FindBar anchors here (a non-scrolling
            ancestor) instead of inside the scrolling <main>, keeping it pinned
            while the document scrolls to a match. */}
        <div className="relative flex min-w-0 flex-1">
          {showEditor && (
            <section
              className={`flex min-w-0 ${
                showPreview
                  ? 'w-1/2 border-r border-neutral-200 dark:border-neutral-800'
                  : 'flex-1'
              }`}
              aria-label="Editor"
            >
              <Editor
                value={editBuffer ?? ''}
                dark={dark}
                onChange={setEditBuffer}
                registerScroller={setEditorScrollEl}
              />
            </section>
          )}
          <main
            ref={docScrollRef}
            className={`mr-main relative min-w-0 overflow-auto ${
              showPreview ? (showEditor ? 'w-1/2' : 'flex-1') : 'hidden'
            } ${readingWidth ? 'mr-reading-width' : ''}`}
          >
            <DefaultAppPrompt
              source={source}
              forceOpen={forceDefaultPrompt}
              onClose={() => {
                setForceDefaultPrompt(false);
              }}
            />
            <div style={{ fontSize: `${String(zoom)}rem` }}>
              {status === 'ready' && render ? (
                <ErrorBoundary resetKey={renderVersion} onReset={openFile}>
                  <DocumentView render={render} />
                </ErrorBoundary>
              ) : status === 'loading' ? (
                <p className="p-8 text-neutral-500">Rendering…</p>
              ) : status === 'error' ? (
                <div className="m-8 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                  <p className="font-semibold">Could not open this document</p>
                  <p className="mt-1 text-sm">{error}</p>
                </div>
              ) : (
                <EmptyState onOpenFile={openFile} />
              )}
            </div>
          </main>
          <FindBar next={next} prev={prev} />
        </div>
      </div>

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => {
          setFeedbackOpen(false);
        }}
        config={config}
      />
    </div>
  );
}
